import {Timestamp} from 'firebase/firestore';

export type FeedbackCategory = 'bug' | 'idea' | 'ux' | 'a11y' | 'other';
export type FeedbackStatus = 'new' | 'triaged' | 'in_progress' | 'done' | 'ignored';
export type FeedbackTeam = 'assimil' | 'assimil_italia' | 'holiwave';
import type {Bilingue} from '../i18n/langue';

export type FeedbackTag =
    'suggestion'
    | 'answer_required'
    | 'forwarded'
    | 'critical'
    | 'duplicate'
    | 'exercises'
    | 'keyboard'
    | 'study'
    | 'progress_evaluation'
    | 'microphone'
    | 'positive'
    | 'revision'
    | 'unhappy'
    // Verification technique (volet 1a). Portent sur CE signalement, pas sur le
    // bug : un signalement non reproduit n'infirme pas l'existence du bug, il
    // dit seulement que ce rapport-la n'a pas pu etre confirme.
    | 'verified'
    | 'not_reproduced'
    // Pose par le diagnostic d'achat quand il a repondu completement : la
    // methode cherchee etait bien la, le message le dit, la demande est close
    // sans que personne l'ait lue. Le clic « non » du message la rouvre et
    // retire cette etiquette — elle affirmerait alors le contraire.
    | 'auto_resolved'
    // Pose par le diagnostic d'achat quand il a REPONDU EN DEMANDANT : la
    // methode cherchee n'a pas ete trouvee, et le message porte le bouton
    // « completer ma demande » — un recu ou un numero de commande
    // debloqueraient la recherche. La balle est donc dans le camp de la
    // personne, et l'arrivee de son complement retire l'etiquette.
    | 'completion_requested'
    // Arrive par la boite support@assimil.app plutot que par le formulaire.
    // La distinction se voit : un courriel n'apporte ni methode, ni boutique,
    // ni numero de commande, et le diagnostic ne peut donc pas en tirer
    // grand-chose.
    | 'email';

export interface FeedbackReport {
  id: string;
  uid: string;
  message: string;
  category: FeedbackCategory;
  rating?: number;
  platform: string;
  appVersion: string;
  buildNumber: string;
  locale: string;
  currentRoute: string;
  betaEnabled: boolean;
  screenshotPath?: string;
  screenshotUrl?: string;
  createdAt: Timestamp;
  // Device fields
  osVersion?: string;
  deviceModel?: string;
  // Champs propres au formulaire web public (source === 'web'). La session y
  // etant anonyme, `contactEmail` est la SEULE adresse ou joindre la personne.
  /** D'ou vient la demande : le formulaire public, ou la boite de support. */
  source?: 'web' | 'email';
  contactEmail?: string;
  /** Sujet choisi sur le formulaire public — plus fin que `category`. */
  topic?: string;
  purchaseChannel?: string;
  method?: string;
  /** L'EAN de la methode, quand elle a ete choisie dans le catalogue. Deux
   *  editions d'une meme langue portent presque le meme titre et deux EAN :
   *  c'est lui qui dit laquelle. */
  methodEan?: string;
  lesson?: string;
  section?: string;
  declaredPlatform?: string;
  /** La personne a confirme etre sur la derniere version publiee. */
  confirmedLatestVersion?: boolean;
  /** Resultat de la recherche automatique lancee avec la demande. */
  diagnosticResult?: 'found' | 'pending' | 'none' | 'error';
  /** Réponse de la personne au « avez-vous retrouvé votre achat ? » de l'e-mail. */
  resolvedAnswer?: 'oui' | 'non';
  diagnosticPurchases?: number;
  diagnosticPending?: number;
  /** Nombre de paiements retrouves chez la boutique (Stripe, Play, App Store). */
  diagnosticStorePayments?: number;
  /** Pose quand le diagnostic a clos la demande de lui-meme. */
  autoResolvedAt?: Timestamp;
  /** Pose quand la personne a repondu « non » a la question de cloture. */
  reopenedAt?: Timestamp;
  /** Objet du courriel, pour les demandes arrivees par la boite. */
  subject?: string;
  /** Nom tel que l'expediteur l'a declare, quand il y en a un. */
  contactName?: string;
  /** Date d'envoi du courriel, distincte de celle de sa lecture. */
  receivedAt?: Timestamp;
  /**
   * Noms des pieces jointes, non reprises.
   *
   * Le contenu reste dans la boite : les rapatrier demanderait un depot et
   * une politique de conservation, et l'equipe a de toute facon acces au
   * message d'origine.
   */
  attachmentNames?: string[];
  /** Demande d'origine, quand celle-ci la complete. */
  relatedFeedbackId?: string;
  orderNumber?: string;
  // Admin fields
  status: FeedbackStatus;
  team?: FeedbackTeam;
  internalNote?: string;
  assignee?: string;
  tags?: FeedbackTag[];
  updatedAt?: Timestamp;
  updatedBy?: string;
  // Conversation fields
  hasUnreadAdminReply?: boolean;
  hasUnreadUserReply?: boolean;
  lastMessageAt?: Timestamp;
  // AI Analysis (populated asynchronously by Cloud Function)
  aiAnalysis?: AiAnalysis;
  /** Bug auquel ce signalement est rattache. Ecrit par createBugFromCandidate,
   *  lu par la fiche pour eviter une requete. Un signalement non rattache n'est
   *  pas pour autant invalide : il peut n'avoir simplement pas encore ete
   *  triage. */
  bugId?: string;
  /** Provenance du tag `verified` / `not_reproduced`. Sans elle, personne ne
   *  peut savoir si le tag vient d'un humain qui a tente de reproduire, ou
   *  d'une regle qui a constate une trace technique. */
  verification?: FeedbackVerification;
}

export interface FeedbackVerification {
  verdict: 'likely_real' | 'unsupported' | 'inconclusive';
  /** Les raisons telles qu'affichees au moment de la decision. */
  reasons: string[];
  tag: FeedbackTag;
  /** 'assisted' : pose depuis le resultat de verifyBugCandidate.
   *  'manual'   : pose a la main par un humain. */
  source: 'assisted' | 'manual';
  verifiedAt: Timestamp;
  verifiedBy: string;
}

export interface ImageAttachment {
  url: string;
  isHero?: boolean;
}

export const MAX_IMAGES_PER_MESSAGE = 4;

/**
 * Normalize legacy single-image `imageUrl` field into the new `images` array.
 * Returns an empty array if neither is set.
 */
export function normalizeImages(
  images: ImageAttachment[] | undefined,
  legacyImageUrl: string | undefined,
): ImageAttachment[] {
  if (images && images.length > 0) return images;
  if (legacyImageUrl) return [{url: legacyImageUrl}];
  return [];
}

// AI Analysis types (written by onFeedbackCreated Cloud Function)
export type RedactReason = 'pii' | 'device' | 'aggressive' | 'irrelevant';
export type RedactSeverity = 'mandatory' | 'suggestion';

export interface AiRedactSuggestion {
  text: string;
  reason: RedactReason;
  severity: RedactSeverity;
}

export interface AiSimilarFeedback {
  feedbackId: string;
  confidence: number;
  matchedTopic: string;
}

export interface AiAnalysis {
  topics: string[];
  redactSuggestions: AiRedactSuggestion[];
  similarFeedbacks: AiSimilarFeedback[];
  model: string;
  analyzedAt: Timestamp;
}

export type MessageAuthorType = 'admin' | 'user' | 'system';

export interface FeedbackMessage {
  id: string;
  authorType: MessageAuthorType;
  /** Objet du courriel, pour les messages envoyes automatiquement. */
  subject?: string;
  /** Le message tel qu'il a ete recu. Present sur les messages `system`. */
  html?: string;
  authorId: string;
  authorName: string;
  content: string;
  /** @deprecated Use `images` instead. Kept for backward compatibility on read. */
  imageUrl?: string;
  images?: ImageAttachment[];
  editedAt?: Timestamp;
  createdAt: Timestamp;
  readAt?: Timestamp;
}

export interface FeedbackFilters {
  category?: FeedbackCategory;
  status?: FeedbackStatus;
  team?: FeedbackTeam;
  tag?: FeedbackTag;
  platform?: string;
  betaEnabled?: boolean;
  startDate?: Date;
  endDate?: Date;
  assignee?: string;
  searchQuery?: string;
  uid?: string;
  fromVersion?: string;
  hasUnreadUserReply?: boolean;
}

export const CATEGORY_LABELS: Record<FeedbackCategory, Bilingue> = {
  bug: {fr: 'Problème', en: 'Problem'},
  idea: {fr: 'Suggestion', en: 'Suggestion'},
  ux: {fr: 'UX (ancien)', en: 'UX (legacy)'},
  a11y: {fr: 'Accessibilité', en: 'Accessibility'},
  other: {fr: 'Autre', en: 'Other'},
};

/**
 * Les sujets, tels que le formulaire les propose. Ils viennent de
 * `FeedbackTopic` cote application : meme identifiant des deux cotes, pour que
 * le back-office n'ait qu'un champ a lire quelle que soit la porte d'entree.
 */
export const TOPIC_LABELS: Record<string, Bilingue> = {
    purchase: {fr: 'Achat non trouvé', en: 'Purchase not found'},
    purchase_failed: {fr: 'Achat impossible', en: 'Purchase failed'},
    account: {fr: 'Connexion impossible', en: 'Cannot sign in'},
    audio: {fr: 'Problème audio', en: 'Audio problem'},
    content: {fr: 'Erreur dans une leçon', en: 'Error in a lesson'},
    crash: {fr: 'Plantage ou lenteur', en: 'Crash or slowness'},
    a11y: {fr: 'Accessibilité', en: 'Accessibility'},
    other_problem: {fr: 'Autre problème', en: 'Other problem'},
};

/**
 * Ce dont parle une demande : son sujet quand elle en a un, sa categorie
 * sinon.
 *
 * La categorie seule ne dit rien d'utile — un achat introuvable s'y affiche
 * « Other », parce que c'est ainsi qu'il se range. Le sujet, lui, nomme ce que
 * la personne a choisi.
 */
export const demandLabel = (item: { topic?: string; category: FeedbackCategory }): Bilingue =>
    (item.topic && TOPIC_LABELS[item.topic]) || CATEGORY_LABELS[item.category];

export const STATUS_LABELS: Record<FeedbackStatus, Bilingue> = {
  new: {fr: 'Nouveau', en: 'New'},
  triaged: {fr: 'Trié', en: 'Triaged'},
  in_progress: {fr: 'En cours', en: 'In progress'},
  done: {fr: 'Traité', en: 'Done'},
  ignored: {fr: 'Ignoré', en: 'Ignored'},
};

export const STATUS_COLORS: Record<FeedbackStatus, string> = {
  new: 'bg-blue-100 text-blue-800',
  triaged: 'bg-yellow-100 text-yellow-800',
  in_progress: 'bg-purple-100 text-purple-800',
  done: 'bg-green-100 text-green-800',
  ignored: 'bg-gray-100 text-gray-800',
};

export const TEAM_LABELS: Record<FeedbackTeam, Bilingue> = {
  assimil: {fr: 'Assimil', en: 'Assimil'},
  assimil_italia: {fr: 'Assimil Italia', en: 'Assimil Italia'},
  holiwave: {fr: 'Holiwave', en: 'Holiwave'},
};

export const TEAM_COLORS: Record<FeedbackTeam, string> = {
  assimil: 'bg-primary/15 text-primary-dark',
  assimil_italia: 'bg-primary/10 text-primary-dark',
  holiwave: 'bg-green-50 text-green-700',
};

export const TAG_LABELS: Record<FeedbackTag, Bilingue> = {
  suggestion: {fr: 'Suggestion', en: 'Suggestion'},
  answer_required: {fr: 'Réponse attendue', en: 'Answer required'},
  forwarded: {fr: 'Transmis', en: 'Forwarded'},
  critical: {fr: 'Critique', en: 'Critical'},
  duplicate: {fr: 'Doublon', en: 'Duplicate'},
  exercises: {fr: 'Exercices', en: 'Exercises'},
  keyboard: {fr: 'Clavier', en: 'Keyboard'},
  study: {fr: 'Étude', en: 'Study'},
  progress_evaluation: {fr: 'Progression / Évaluation', en: 'Progress / Evaluation'},
  microphone: {fr: 'Micro', en: 'Microphone'},
  positive: {fr: 'Positif', en: 'Positive'},
  revision: {fr: 'Révision', en: 'Revision'},
  unhappy: {fr: 'Mécontent', en: 'Unhappy'},
  verified: {fr: 'Vérifié', en: 'Verified'},
  not_reproduced: {fr: 'Non reproduit', en: 'Not reproduced'},
  auto_resolved: {fr: 'Résolu automatiquement', en: 'Auto-resolved'},
  completion_requested: {fr: 'Complément demandé', en: 'Completion requested'},
  email: {fr: 'E-mail', en: 'Email'},
};

/**
 * Les memes etiquettes, pleines — pour celle qu'on a retenue.
 *
 * Ecrites en toutes lettres, et non derivees de TAG_COLORS par
 * substitution : Tailwind ne genere que les classes qu'il LIT dans le
 * source. Une classe construite a l'execution n'existe pas dans la feuille
 * de style, et la pastille resterait blanche.
 */
export const TAG_COLORS_SELECTED: Record<FeedbackTag, string> = {
  suggestion: 'bg-primary text-white',
  answer_required: 'bg-accent text-white',
  forwarded: 'bg-accent text-white',
  critical: 'bg-danger text-white',
  duplicate: 'bg-gray-500 text-white',
  exercises: 'bg-primary text-white',
  keyboard: 'bg-primary text-white',
  study: 'bg-primary text-white',
  progress_evaluation: 'bg-primary text-white',
  microphone: 'bg-primary text-white',
  positive: 'bg-success text-white',
  revision: 'bg-primary text-white',
  unhappy: 'bg-danger text-white',
  verified: 'bg-success text-white',
  not_reproduced: 'bg-gray-500 text-white',
  auto_resolved: 'bg-success text-white',
  completion_requested: 'bg-primary text-white',
  email: 'bg-gray-500 text-white',
};

export const TAG_COLORS: Record<FeedbackTag, string> = {
  suggestion: 'bg-primary/10 text-primary-dark',
  answer_required: 'bg-accent/10 text-accent-dark',
  forwarded: 'bg-accent/10 text-accent-dark',
  critical: 'bg-danger/10 text-danger-dark',
  duplicate: 'bg-gray-100 text-gray-600',
  exercises: 'bg-primary/10 text-primary-dark',
  keyboard: 'bg-primary/10 text-primary-dark',
  study: 'bg-primary/10 text-primary-dark',
  progress_evaluation: 'bg-primary/10 text-primary-dark',
  microphone: 'bg-primary/10 text-primary-dark',
  positive: 'bg-success/10 text-success-dark',
  revision: 'bg-primary/10 text-primary-dark',
  unhappy: 'bg-danger/10 text-danger-dark',
  verified: 'bg-success/10 text-success-dark',
  not_reproduced: 'bg-gray-100 text-gray-600',
  auto_resolved: 'bg-success/10 text-success-dark',
  // Ce que l'equipe doit comprendre d'un coup d'oeil : il n'y a rien a faire
  // sur cette demande tant que le recu ou le numero ne sont pas arrives.
  //
  // En bleu, et non dans le corail de l'application : ici l'orange voisine
  // avec « Answer Required » et « Exercices », et trois chaleurs cote a cote
  // ne se distinguent plus. La couleur d'un etat vaut par son ecart avec
  // celles d'a cote, pas dans l'absolu.
  completion_requested: 'bg-primary/10 text-primary-dark',
  email: 'bg-gray-100 text-gray-600',
};

export const ALL_TEAMS: FeedbackTeam[] = ['assimil', 'assimil_italia', 'holiwave'];
export const ALL_TAGS: FeedbackTag[] = ['suggestion', 'answer_required', 'forwarded', 'critical', 'duplicate', 'exercises', 'keyboard', 'study', 'progress_evaluation', 'microphone', 'positive', 'revision', 'unhappy', 'verified', 'not_reproduced', 'auto_resolved', 'email'];

// Team Members (Assignees)
/**
 * Entreprise d'appartenance. Les identifiants reprennent l'enum `UserGroup`
 * du back-office — 1 Holiwave, 2 Assimil — pour que les deux systemes nomment
 * la meme realite de la meme facon.
 */
export type UserGroup = 'HOLIWAVE' | 'ASSIMIL';
export const USER_GROUP_IDS: Record<UserGroup, number> = {HOLIWAVE: 1, ASSIMIL: 2};

/**
 * Une personne de l'equipe, et les droits qu'on lui accorde.
 *
 * Le document est cle par **email en minuscules** : c'est ce qui permet a
 * l'application mobile de lire sa propre ligne sans requete, les regles
 * Firestore comparant l'identifiant a l'email du jeton. Ne jamais recreer un
 * document avec un identifiant automatique — il deviendrait invisible pour
 * l'app, sans que rien ne le signale.
 */
export interface TeamMember {
  id: string;            // email en minuscules
  email: string;
  name: string;
  team: FeedbackTeam;    // qui traite le retour

  /** Entreprise : 1 Holiwave, 2 Assimil. L'entier fait foi. */
  userGroupId?: number;
  /** Le nom du groupe, pour la lisibilite dans la console. */
  userGroup?: UserGroup;

  /** Apparait-il dans la liste des assignes ? */
  canBeAssigned?: boolean;
  /** Mode support et contenu de test dans l'application. */
  hasSupportAccess?: boolean;
  /** Acces au Pack Immersion sans abonnement. */
  hasImmersion?: boolean;

  createdAt: Timestamp;
  updatedAt?: Timestamp;
}

// App Versions by platform
export type AppPlatform = 'ios' | 'android' | 'macos' | 'windows' | 'web';

export interface AppVersions {
  ios?: string;
  android?: string;
  macos?: string;
  windows?: string;
  web?: string;
  updatedAt?: Timestamp;
}

// Map feedback platform strings to AppPlatform keys
export const PLATFORM_MAP: Record<string, AppPlatform | undefined> = {
  'iOS': 'ios',
  'ios': 'ios',
  'Android': 'android',
  'android': 'android',
  'macOS': 'macos',
  'MacOS': 'macos',
  'macos': 'macos',
  'Windows': 'windows',
  'windows': 'windows',
  'Web': 'web',
  'web': 'web',
};

export const PLATFORM_DISPLAY_NAMES: Record<AppPlatform, string> = {
  ios: 'iOS',
  android: 'Android',
  macos: 'macOS',
  windows: 'Windows',
  web: 'Web',
};

export const ALL_PLATFORMS: AppPlatform[] = ['ios', 'android', 'macos', 'windows', 'web'];

/**
 * Nom lisible d'une plateforme, quelle que soit son ecriture d'origine.
 *
 * Une valeur inconnue est rendue telle quelle : la taire ferait disparaitre
 * une plateforme reellement touchee.
 */
export function platformLabel(raw: string): string {
  const key = PLATFORM_MAP[raw] ?? PLATFORM_MAP[raw.toLowerCase()];
  return key ? PLATFORM_DISPLAY_NAMES[key] : raw;
}
